import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/TermsConditions.css';

const TermsConditions = () => {
  const navigate = useNavigate();

  // ✅ Added scrollTop function
  const scrollTop = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <div className="terms-conditions-container">
      {/* Header Section */}
      <div className="terms-conditions-header">
        <h1 className="terms-conditions-title">Terms & Conditions</h1>
        <div className="breadcrumb">
          <button className="breadcrumb-home" onClick={() => scrollTop('/')}>
            Home
          </button>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-current">Terms & Conditions</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="terms-conditions-content">
        <div className="terms-conditions-text">
          <p className="last-updated">
            <strong>Last Updated:</strong> December 2024
          </p>
          
          <p>
            Welcome to <strong>HotelPlacement.ae</strong>. These Terms and Conditions ("Terms") govern your use of our hospitality recruitment platform and services. By accessing or using our platform, you agree to be bound by these Terms.
          </p>

          <h2 className="section-title">1. Acceptance of Terms</h2>
          
          <p>
            By creating an account, accessing our platform, or using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, please do not use our platform.
          </p>

          <h2 className="section-title">2. Description of Service</h2>
          
          <p>
            HotelPlacement.ae is a comprehensive hospitality recruitment platform that provides:
          </p>
          <ul className="policy-list">
            <li>Job posting and application services for hospitality positions</li>
            <li>Candidate profile management and matching services</li>
            <li>Employer recruitment tools and applicant tracking</li>
            <li>Assessment tools and talent community access</li>
            <li>Onboarding support for new hires</li>
            <li>Educational resources and career guidance</li>
          </ul>

          <h2 className="section-title">3. User Accounts and Registration</h2>
          
          <h3 className="subsection-title">Account Creation</h3>
          <p>To use our platform, you must:</p>
          <ul className="policy-list">
            <li>Provide accurate, complete, and current information</li>
            <li>Maintain and update your account information</li>
            <li>Be at least 18 years old</li>
            <li>Have the legal capacity to enter into these Terms</li>
            <li>Maintain the security of your account credentials</li>
          </ul>

          <h3 className="subsection-title">Account Responsibilities</h3>
          <p>You are responsible for:</p>
          <ul className="policy-list">
            <li>All activities that occur under your account</li>
            <li>Maintaining the confidentiality of your password</li>
            <li>Notifying us immediately of any unauthorized use</li>
            <li>Ensuring your profile information is truthful and accurate</li>
          </ul>

          <h2 className="section-title">4. User Conduct and Prohibited Activities</h2>
          
          <p>You agree not to:</p>
          <ul className="policy-list">
            <li>Post false, misleading, or fraudulent information</li>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Harass, abuse, or harm other users</li>
            <li>Attempt to gain unauthorized access to our systems</li>
            <li>Use automated tools to access our platform</li>
            <li>Post inappropriate, offensive, or illegal content</li>
            <li>Interfere with the proper functioning of our platform</li>
          </ul>

          <h2 className="section-title">5. Job Postings and Applications</h2>
          
          <h3 className="subsection-title">For Employers</h3>
          <p>Employers agree to:</p>
          <ul className="policy-list">
            <li>Post only legitimate job openings</li>
            <li>Provide accurate job descriptions and requirements</li>
            <li>Comply with all applicable employment laws</li>
            <li>Respond to applications in a timely manner</li>
            <li>Maintain confidentiality of candidate information</li>
          </ul>

          <h3 className="subsection-title">For Job Seekers</h3>
          <p>Job seekers agree to:</p>
          <ul className="policy-list">
            <li>Provide accurate and complete profile information</li>
            <li>Submit only genuine applications</li>
            <li>Respect employer confidentiality requirements</li>
            <li>Follow up on applications professionally</li>
          </ul>

          <h2 className="section-title">6. Intellectual Property Rights</h2>
          
          <p>
            All content on our platform, including text, graphics, logos, images, and software, is the property of HotelPlacement.ae or its licensors and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our written permission.
          </p>

          <h2 className="section-title">7. Privacy and Data Protection</h2>
          
          <p>
            Your privacy is important to us. Our collection and use of personal information is governed by our Privacy Policy, which is incorporated into these Terms by reference. By using our platform, you consent to the collection and use of your information as described in our Privacy Policy.
          </p>

          <h2 className="section-title">8. Payment Terms</h2>
          
          <p>
            Some services on our platform may require payment. All fees are non-refundable unless otherwise specified. We reserve the right to change our pricing at any time with reasonable notice.
          </p>

          <h2 className="section-title">9. Service Availability</h2>
          
          <p>
            We strive to maintain continuous service availability but cannot guarantee uninterrupted access. We may temporarily suspend services for maintenance, updates, or technical issues. We are not liable for any downtime or service interruptions.
          </p>

          <h2 className="section-title">10. Limitation of Liability</h2>
          
          <p>
            To the maximum extent permitted by law, HotelPlacement.ae shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business opportunities, arising from your use of our platform.
          </p>

          <h2 className="section-title">11. Indemnification</h2>
          
          <p>
            You agree to indemnify and hold harmless HotelPlacement.ae, its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of our platform or violation of these Terms.
          </p>

          <h2 className="section-title">12. Termination</h2>
          
          <p>
            We may terminate or suspend your account at any time for violation of these Terms or for any other reason at our sole discretion. You may also terminate your account at any time by contacting us.
          </p>

          <h2 className="section-title">13. Dispute Resolution</h2>
          
          <p>
            Any disputes arising from these Terms or your use of our platform shall be resolved through binding arbitration in accordance with the laws of the United Arab Emirates. The arbitration shall be conducted in Dubai, UAE.
          </p>

          <h2 className="section-title">14. Changes to Terms</h2>
          
          <p>
            We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on our platform. Your continued use of our platform after such changes constitutes acceptance of the updated Terms.
          </p>

          <h2 className="section-title">15. Contact Information</h2>
          
          <p>
            If you have any questions about these Terms and Conditions, please contact us:
          </p>
          <div className="contact-info">
            <p><strong>Email:</strong> info@hotelplacement.ae</p>
            <p><strong>Phone:</strong> 971-42503121</p>
            <p><strong>Address:</strong> Aspin Towers, Sheikh Zayed Road, Dubai, UAE</p>
          </div>

          <p className="effective-date">
            <strong>Effective Date:</strong> These Terms and Conditions are effective as of December 2024.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="terms-conditions-footer">
        <div className="footer-content">
          <div className="footer-column">
            <h3>Contact Us</h3>
            <div className="contact-item">
              <span className="contact-icon">📍</span>
              <span>Aspin Towers, Sheikh Zayed Road, Dubai, UAE</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">📱</span>
              <span>971-42503121</span>
            </div>
            <div className="contact-item">
              <span className="contact-icon">✉️</span>
              <span>info@hotelplacement.ae</span>
            </div>
          </div>
          <div className="footer-column">
            <h3>Company & Policy</h3>
            <div className="policy-links">
              <button className="policy-link" onClick={() => scrollTop('/AboutUs')}>About Us</button>
              <button className="policy-link" onClick={() => scrollTop('/')}>Home</button>
              <button className="policy-link" onClick={() => scrollTop('/faq')}>FAQs</button>
              <button className="policy-link" onClick={() => scrollTop('/privacy-policy')}>Privacy Policy</button>
              <button className="policy-link active">Terms & Condition</button>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p>&copy; 2024 HotelPlacement.ae. All rights reserved.</p>
            <div className="footer-social">
              <span>Follow us:</span>
              {/* ✅ FIXED: Added valid hrefs and security attributes */}
              <a 
                href="https://www.linkedin.com" 
                className="social-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>
              <a 
                href="https://twitter.com" 
                className="social-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Twitter
              </a>
              <a 
                href="https://www.facebook.com" 
                className="social-link"
                target="_blank" 
                rel="noopener noreferrer"
              >
                Facebook
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;